import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";
import { api, bannerImageUrl } from "@/lib/appresse";

// Fallback slides shown only when the admin has not configured any banner yet.
const DEFAULT_SLIDES = [
  { img: "/hero1.webp", title: "Des regards sur un monde en mouvement", tags: ["S'INFORMER", "COMPRENDRE", "S'ÉVADER"] },
  { img: "/hero2.webp", title: "L'économie décryptée, chaque jour", tags: ["ÉCONOMIE", "SOCIÉTÉ", "POLITIQUE"] },
  { img: "/hero3.webp", title: "L'ailleurs commence ici", tags: ["VOYAGE", "CULTURE", "INSPIRATION"] },
];

export const HeroCarousel = () => {
  const [banners, setBanners] = useState(null); // null = loading
  const [i, setI] = useState(0);
  const [unmuted, setUnmuted] = useState(false);
  const seenRef = useRef(new Set());
  const videoRef = useRef(null);

  useEffect(() => {
    api.publicBanners()
      .then((d) => setBanners(d.banners || []))
      .catch(() => setBanners([]));
  }, []);

  const useBanners = Array.isArray(banners) && banners.length > 0;
  const slides = useBanners
    ? banners.map((b) => ({ id: b.id, img: `${bannerImageUrl(b.id)}?v=${b.media_v || ""}`, title: b.title, link: b.link_url, media: b.media_type, sound: b.sound }))
    : DEFAULT_SLIDES;
  const count = slides.length;

  useEffect(() => {
    if (count <= 1) return;
    const t = setInterval(() => setI((p) => (p + 1) % count), 6000);
    return () => clearInterval(t);
  }, [count]);

  useEffect(() => { setI(0); }, [useBanners, count]);

  // Each slide starts muted; unmute is per-slide.
  useEffect(() => { setUnmuted(false); }, [i]);

  const current = slides[i] || slides[0];
  useEffect(() => {
    if (!useBanners || !current?.id) return;
    if (seenRef.current.has(current.id)) return;
    seenRef.current.add(current.id);
    api.bannerEvent(current.id, "impression").catch(() => {});
  }, [useBanners, current]);

  // React does not reliably reflect `muted` prop changes on <video>; drive it imperatively.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const shouldMute = !(current?.sound && unmuted);
    v.muted = shouldMute;
    if (!shouldMute) v.play().catch(() => {});
  }, [unmuted, current, i]);

  if (banners === null) {
    return <div data-testid="hero-carousel" className="rounded-2xl overflow-hidden border border-border aspect-[16/9] sm:aspect-[21/9] bg-gradient-to-br from-[#0A2472] to-[#06122e]" />;
  }

  const s = current;
  const isVideo = useBanners && s?.media === "video";
  const clickable = useBanners && Boolean(s?.link);

  const handleClick = () => {
    if (!clickable) return;
    api.bannerEvent(s.id, "click").catch(() => {});
    window.open(s.link, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      data-testid="hero-carousel"
      onClick={clickable ? handleClick : undefined}
      role={clickable ? "link" : undefined}
      className={`relative rounded-2xl overflow-hidden border border-border shadow-[0_12px_32px_-6px_rgba(0,0,0,0.45)] aspect-[16/9] sm:aspect-[21/9] bg-gradient-to-br from-[#0A2472] to-[#06122e] ${clickable ? "cursor-pointer" : ""}`}
    >
      <AnimatePresence mode="wait">
        <motion.div key={i} initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }} transition={{ duration: 0.6 }} className="absolute inset-0">
          {isVideo ? (
            <video
              ref={videoRef}
              src={s.img}
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              className="w-full h-full object-cover"
              data-testid="hero-video"
            />
          ) : (
            <img src={s.img} alt={s.title || "Publicité"} loading="eager" fetchPriority="high" decoding="async"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
              className="w-full h-full object-cover" />
          )}
          {(s.title || !useBanners) && (
            <div className="absolute inset-0 bg-gradient-to-t from-[#06122e]/90 via-[#06122e]/40 to-transparent" />
          )}
        </motion.div>
      </AnimatePresence>

      {isVideo && s.sound && (
        <button
          onClick={(e) => { e.stopPropagation(); setUnmuted((v) => !v); }}
          aria-label={unmuted ? "Couper le son" : "Activer le son"}
          data-testid="hero-sound-toggle"
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/50 backdrop-blur flex items-center justify-center text-white hover:bg-black/70 transition-colors"
        >
          {unmuted ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
        </button>
      )}

      {s.title && (
        <div className="absolute inset-0 p-5 sm:p-7 flex flex-col justify-end pointer-events-none">
          <h2 className="font-serif font-black text-white text-2xl sm:text-3xl max-w-md leading-tight drop-shadow">
            {s.title}
          </h2>
          {s.tags && (
            <div className="flex gap-2 mt-3 flex-wrap">
              {s.tags.map((t) => (
                <span key={t} className="text-[10px] font-mono tracking-wider text-white/90 border border-white/40 rounded-full px-2.5 py-1">
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {!useBanners && (
        <div className="absolute top-4 right-4 text-right pointer-events-none">
          <span className="font-serif text-white text-sm">
            <span className="font-black">APP</span> <span className="font-semibold">PRESSE</span>
          </span>
          <p className="text-[9px] text-white/70 font-mono tracking-widest">LA PRESSE AUTREMENT</p>
        </div>
      )}

      {count > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
          {slides.map((_, idx) => (
            <button key={idx} onClick={(e) => { e.stopPropagation(); setI(idx); }} aria-label={`Bannière ${idx + 1}`}
              className={`h-1.5 rounded-full transition-all ${idx === i ? "w-6 bg-white" : "w-1.5 bg-white/50"}`} />
          ))}
        </div>
      )}
    </div>
  );
};
